import { useState, useEffect } from 'react';
import { MainHeaderDropdown, MainHeaderDropdownItem } from 'palette/MainHeaderDropdown';
import { MainHeaderNavBar } from 'palette/MainHeaderNavBar';
import { useModal } from 'palette/Modal';
import { getGender } from '@common/Brands/helpers/lastGender';
import BurgerNavButton from '@common/BurgerNavigation/BurgerNavButton';
import { BurgerNavigation } from '@common/BurgerNavigation/BurgerNavigation';
import { themeColor } from '@common/constants';
import GlobalSearchContainer from '@common/GlobalSearch/containers/GlobalSearchContainer';
import { useConfig } from '@common/Hooks/useConfig';
import { get } from '@common/Http';
import { GA_SEARCH_CATEGORY, GA_SEARCH_HEADER_LABEL, GA_SEARCH_OPEN_ACTION } from '@common/Tracking';
import { menuSelectItemListTracking, burgerMenuToggleTracker } from '@common/Tracking/Navigation';
import { BEMBlock } from '@helpers/classnames';
import { parseJSON } from '@helpers/data';
import { pushGAEventToDataLayer } from '@helpers/dataLayer';
import { isBoozt, isBooztletAndWebshop } from '@helpers/env';
import { loadSession, saveSession } from '@helpers/sessionStorage';
import { getTranslatedPropertyFromContext } from '@helpers/translationContext';
import { NAVIGATION_LOAD_JSON } from '@routes';
import { MainHeaderLogo } from '../components/MainHeaderLogo';
import { getHeaderLogoName } from '../helpers/getHeaderLogoName';
import { useMainHeaderNavBar } from '../hooks/useMainHeaderNavBar';
import { MainHeaderActionBar } from './MainHeaderActionBar';
import { HEADER_DROPDOWN_IDS, DROPDOWN_MENU_MODAL_ID } from './PageHeaderConstants';
import '../styles/MainHeader.scss';

const modalId = 'burgerNavigationModal';

export function MainHeader({
    topMenuTree,
    customerDashboardUrl,
    checkoutShopcartUrl,
    currentDepartment,
    imagesSourceUrl,
    uuids,
    customerServicePageUuids,
    tabIds,
    unisexTabIds,
    languages,
    onMainHeaderNavBarHover,
    preloadedNavigation = null,
    breadcrumbs,
    activeModalId,
    activeDropdownId,
    translatedUSPS,
    hoveredMainHeaderNavId,
    selectedDepartment,
    setSelectedDepartment,
    v2
}) {
    const config = useConfig();
    const [isSearchActive, setIsSearchActive] = useState(false);
    const { setActiveModalId } = useModal();
    const [navigation, setNavigation] = useState(null);
    const [serviceSection, setServiceSection] = useState(null);
    const { handleMainHeaderNavBarLinkClick, mainHeaderNavBarItems } = useMainHeaderNavBar(
        topMenuTree, setSelectedDepartment
    );
    const { store } = config;
    const hasPreLoadedData = preloadedNavigation !== null;
    const serviceDataKey = `service_data_${store}`;
    const navDataKey = `nav_data_${store}`;
    const isBooztletEnv = isBooztletAndWebshop(config);
    const logoAlt = isBooztletEnv ? 'Booztlet' : 'Boozt';
    const logo = getHeaderLogoName(isBooztletEnv, store);
    const theme = !isBooztletEnv ? themeColor.light : themeColor.gray;
    const isHeaderNavModalActive = activeModalId === DROPDOWN_MENU_MODAL_ID;
    const [block, BEMElement] = BEMBlock('header-main', { preloaded: hasPreLoadedData });

    const handleFocus = () => {
        if (!isSearchActive) {
            pushGAEventToDataLayer(GA_SEARCH_CATEGORY, GA_SEARCH_OPEN_ACTION, GA_SEARCH_HEADER_LABEL);
        }

        setIsSearchActive(true);
    };

    const handleBlur = () => {
        setIsSearchActive(false);
    };

    const handleMainHeaderNavBarHover = (navItem) => {
        onMainHeaderNavBarHover?.(navItem);
    };

    const handleMainHeaderDropdownLinkClick = (navItem) => {
        const trackingNavItem = navItem;
        const hoveredDepartment = topMenuTree.find(department => department.navigationId === hoveredMainHeaderNavId);
        const newSelectedDepartment = { id: hoveredMainHeaderNavId, name: hoveredDepartment.languageContext.en?.name };
        setSelectedDepartment(newSelectedDepartment);
        trackingNavItem.lvl = 2;
        menuSelectItemListTracking(trackingNavItem, newSelectedDepartment);
    };

    const fetchNavData = async () => {
        let navData;

        try {
            navData = hasPreLoadedData ? preloadedNavigation : await get(NAVIGATION_LOAD_JSON, { mobile: 1 });
        } catch (error) {}

        if (navData) {
            const currentNav = Object.values(navData.children).find(item => item.gender === getGender());
            const { titleUuid, pageUuid } = parseJSON(customerServicePageUuids) ?? {};
            const servicedata = {
                title: currentNav.children[titleUuid],
                customerService: currentNav.children[pageUuid]
            };

            if (!hasPreLoadedData) {
                saveSession(serviceDataKey, servicedata);
                saveSession(navDataKey, navData);
            }

            setServiceSection(servicedata);
            setNavigation(navData);
        }
    };

    const getNavData = () => {
        if (hasPreLoadedData) {
            fetchNavData();
            return;
        }

        const sessionNavData = loadSession(navDataKey);
        const sessionServiceData = loadSession(serviceDataKey);

        if (
            typeof sessionNavData !== 'undefined'
            && typeof sessionServiceData !== 'undefined'
            && Object.keys(sessionServiceData).length !== 0
        ) {
            setServiceSection(sessionServiceData);
            setNavigation(sessionNavData);
        } else {
            fetchNavData();
        }
    };

    const toggleMenu = (open) => {
        if (open) {
            setActiveModalId(modalId);
            burgerMenuToggleTracker(open);
        } else {
            setActiveModalId(null);
            burgerMenuToggleTracker();
        }

        if (open && !navigation) {
            getNavData();
        }
    };

    useEffect(() => {
        if (!isBoozt()) {
            getNavData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={block}>
            <nav className={BEMElement('burger-navigation')}>
                <BurgerNavButton
                    setIsNavOpen={toggleMenu}
                />
                <BurgerNavigation
                    breadcrumbs={breadcrumbs}
                    customerServicePageUuids={customerServicePageUuids}
                    imagesSourceUrl={imagesSourceUrl}
                    languages={languages}
                    menuItems={mainHeaderNavBarItems}
                    modalId={modalId}
                    navigation={navigation}
                    selectedDepartment={selectedDepartment}
                    serviceSection={serviceSection}
                    setSelectedDepartment={setSelectedDepartment}
                    tabIds={tabIds}
                    toggleMenu={toggleMenu}
                    unisexTabIds={unisexTabIds}
                    uuids={uuids}
                />
            </nav>
            <MainHeaderNavBar
                className={BEMElement('main-header-nav-bar')}
                hoveredMainHeaderNavId={hoveredMainHeaderNavId}
                menuItems={mainHeaderNavBarItems}
                onLinkClick={handleMainHeaderNavBarLinkClick}
                onLinkHover={handleMainHeaderNavBarHover}
            />
            <MainHeaderDropdown
                footerData={translatedUSPS}
                isOpen={isHeaderNavModalActive && activeDropdownId === HEADER_DROPDOWN_IDS.MAIN_HEADER}
                v2={v2}
            >
                {/* Mounting all children in DOM for SEO purposes */}
                {topMenuTree.map(navItem => navItem.children.map((menuItem) => {
                    const headerMenuItemClasses = BEMElement('menu-item', {
                        hidden: hoveredMainHeaderNavId !== navItem.navigationId
                    });

                    return (
                        <MainHeaderDropdownItem
                            key={menuItem.navigationId}
                            className={headerMenuItemClasses}
                            id={menuItem.navigationId}
                            image={menuItem.image}
                            name={getTranslatedPropertyFromContext(menuItem, 'name', config)}
                            url={config.URLPrefix + menuItem.url}
                            onClick={() => handleMainHeaderDropdownLinkClick(menuItem)}
                        />
                    );
                }))}
            </MainHeaderDropdown>
            <MainHeaderLogo
                ariaLabel={logoAlt}
                className={BEMElement('logo')}
                href={config.URLPrefix}
                logo={logo}
            />
            <div className={BEMElement('search')}>
                <GlobalSearchContainer
                    hasCloseButton
                    currentDepartment={currentDepartment}
                    isActive={isSearchActive}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                />
            </div>
            <div className={BEMElement('icons')}>
                <MainHeaderActionBar
                    checkoutShopcartUrl={checkoutShopcartUrl}
                    customerDashboardUrl={customerDashboardUrl}
                    theme={theme}
                />
            </div>
        </div>
    );
}
